import React, { useState, useContext, useRef } from 'react';
import _merge from 'lodash/merge';
import useCombinedRefs from 'common/useCombinedRefs';
import getContext from 'common/get-context';
import KEY_CODES from 'common/KeyCodes';

import { SearchwChipsContext } from 'components/SearchwChips';

import RippleEffect, { RippleEffectProps, RippleEffectHandle } from '../RippleEffect';
import ChipIconButton from './ChipIconButton';
import ChipLabel from './ChipLabel';
import ChipIcon from './ChipIcon';
import Styled from './styles';


const DEFAULT_SIZE = 'medium';

const MEDIUM_HEIGHT = '2rem';
const SMALL_HEIGHT = '1.25rem';

const {
  ENTER_KEY,
  SPACE_KEY,
} = KEY_CODES;
const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);

const GET_BORDER_RADIUS = props => `${props.size === 'small' ? `calc(${SMALL_HEIGHT} / 2)` : `calc(${MEDIUM_HEIGHT} / 2)`}`;

export interface ChipProps extends React.ComponentPropsWithRef<'div'> {
  /** The size of the Chip. If size is not set, it will be internally taken from the immediate parent `SearchwChips` component, if such parent doesn't exist `size` will be `medium`. */
  size?: 'small' | 'medium';
  /** If `true`, component will be rendered in focused state. */
  autoFocus?: boolean;
  /** If `true`, component will be rendered with selected visual. */
  selected?: boolean;
  /** If `true`, component will be rendered in error state. */
  error?: boolean;
  /** The content of the chip, including `<ChipLabel />`,  `<ChipAvatar />` and `<ChipIcon />` */
  children: React.ReactNode;
  /** The role of the chip element. */
  role?: string;
  /** To alter the ripple effect DOM element (the outermost element) attributes. */
  rippleProps?: Partial<RippleEffectProps>;
  /** The outermost element style. (RippleEffect container) */
  style?: React.CSSProperties;
  /** Style of the Chip. Most of the visuals can be overridden by this prop. */
  chipStyle?: React.CSSProperties;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
  /** @ignore */
  onMouseOver?: React.MouseEventHandler;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
  /** @ignore */
  onMouseUp?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onKeyUp?: React.KeyboardEventHandler;
}

interface ChipContextInterface extends Omit<ChipProps, 'children'> {
  children?: React.ReactNode;
  hovered?: boolean;
  focused?: boolean;
  chipRef?: React.RefObject<HTMLElement>
  isPressed?: boolean;
}

const ChipContext = React.createContext<ChipContextInterface | null>({});

/**
 * Chip element is wrapped with RippleEffect, so to alter the outermost element attributes (ripple effect DOM element) use `rippleProps`.
 * Any other props than what listed below such as, onClick and onKeyDown will be provided to the chip element.
 * `chipRef`, `selected`, `error`, `size`, `hovered` and `focused` are shared with nested elements using `ChipContext`.
 */
const Chip = React.forwardRef<HTMLElement, ChipProps>((props: ChipProps, ref) => getContext(({ RTL, theme }) => {
  const {
    onMouseLeave,
    onMouseOver,
    onMouseDown,
    onKeyDown,
    onKeyUp,
    onMouseUp,
    onFocus,
    onBlur,
    autoFocus,
    children,
    size,
    error,
    selected,
    style,
    rippleProps,
    chipStyle,
    theme: themeProp,
    ...otherProps
  } = props;

  const containerRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, containerRef);

  const rippleRef = useRef<RippleEffectHandle>(null);

  const resolvedTheme = _merge({}, theme.CHIP, themeProp);
  const activeTheme = error ? selected ? resolvedTheme.errorSelected : resolvedTheme.error : selected ? resolvedTheme.selected : resolvedTheme.default;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(autoFocus);
  const [isPressed, setIsPressed] = useState(false);

  let resolvedSize = size;
  const { size: inheritedSize } = useContext(SearchwChipsContext) || {};
  resolvedSize = size || inheritedSize || DEFAULT_SIZE;

  const wrapperProps = {
    onMouseOver: (event) => { setHovered(true); onMouseOver && onMouseOver(event); },
    onMouseDown: (event) => { setIsPressed(true); onMouseDown && onMouseDown(event); },
    onMouseLeave: (event) => { setHovered(false); setIsPressed(false); onMouseLeave && onMouseLeave(event); },
    onFocus: (event) => { setFocused(true); onFocus && onFocus(event); },
    onBlur: (event) => { setFocused(false); setIsPressed(false); onBlur && onBlur(event); },
    onKeyDown: (event) => { isSelectionKeyPressed(event.key) && setIsPressed(true); onKeyDown && onKeyDown(event); },
    onKeyUp: (event) => { setIsPressed(false); onKeyUp && onKeyUp(event); },
    onMouseUp: (event) => { setIsPressed(false); onMouseUp && onMouseUp(event); },
    hovered,
    focused,
    tabIndex: 0,
    error,
    size: resolvedSize,
    selected,
    ...otherProps
  };
  return (
    <RippleEffect
      ref={rippleRef}
      rippleStyle={{
        backgroundColor: activeTheme.rippleColor,
      }}
      style={{
        borderRadius: GET_BORDER_RADIUS(props),
        flex: '0 1 auto',
        width: 'min-content',
        minWidth: 'fit-content',
        ...style
      }}
      {...rippleProps}
    >
      <ChipContext.Provider
        value={{
          ...props,
          hovered,
          focused,
          error,
          selected,
          chipRef: containerRef,
          size: resolvedSize,
          isPressed,
        }}
      >
        <Styled.Chip
          $theme={activeTheme}
          ref={combinedRef}
          RTL={RTL}
          style={chipStyle}
          {...wrapperProps}
        >
          {children}
        </Styled.Chip>
      </ChipContext.Provider>
    </RippleEffect>
  );
}));

Chip.displayName = 'Chip';

Chip.defaultProps = {
  size: undefined,
  selected: false,
  error: false,
  role: 'button',
  rippleProps: {},
  style: {},
  chipStyle: {},
  theme: {},
  autoFocus: false,
  onMouseLeave: undefined,
  onMouseOver: undefined,
  onMouseDown: undefined,
  onMouseUp: undefined,
  onKeyDown: undefined,
  onKeyUp: undefined,
  onFocus: undefined,
  onBlur: undefined,
};

export { Chip as default, ChipLabel, ChipIcon, ChipContext, ChipIconButton };
